import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWithActivity } from '../../utils/fetchWithActivity';
import { suggestionDeepLink } from '../../utils/deepLinks';
import WorkflowSuggestionCards, { WorkflowSuggestion } from '../WorkflowSuggestionCards';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const POLL_INTERVAL_MS = 2500;
// Soft cap: stop blocking the UI on "Reading…" copy and let the user type
// the customer manually — but keep polling in the background past this
// point (see the hard cap) so a slow extraction still updates the modal
// once it actually finishes.
const EXTRACTION_SOFT_WAIT_MS = 90 * 1000;
// Hard cap: stop polling altogether — a safety valve against polling
// forever if the modal is left open with an extraction that never resolves.
const EXTRACTION_HARD_STOP_MS = 10 * 60 * 1000;

interface IntakeDocumentStatus {
  id: string;
  name: string;
  extractionStatus: string;
  namedInsured: string | null;
  carrier: string | null;
  lineOfBusiness: string | null;
  documentKind: string | null;
  effectiveDate: string | null;
  expirationDate: string | null;
  suggestedTitle: string;
}

interface CustomerMatch {
  customerId: string;
  name: string;
  score: number;
}

interface IntakeStatus {
  documents: IntakeDocumentStatus[];
  pendingCount: number;
  candidate: {
    extractedName: string | null;
    conflict: boolean;
    matches: CustomerMatch[];
    suggestedProjectName: string;
  };
}

interface UploadingFile {
  localId: string;
  file: File;
  status: 'uploading' | 'uploaded' | 'error';
  documentId?: string;
  errorMessage?: string;
}

interface RenameEntry {
  value: string;
  touched: boolean;
}

interface CommitResult {
  customerId: string;
  projectId: string;
  suggestions: WorkflowSuggestion[];
}

function authHeaders(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function stripExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(0, idx) : name;
}

interface IntakeModalProps {
  files: File[];
  source: 'home' | 'assistant';
  onClose: () => void;
}

/**
 * Three-step "drop files anywhere, get a filed project" wizard
 * (SMART_INTAKE_PLAN.md Phase 2). Owns its own intake session end-to-end:
 * uploads to the staging area, polls extraction status, lets the user
 * confirm/rename/pick a customer, then commits — filing everything into a
 * real customer + folder in one call and showing the resulting workflow
 * recommendations right here.
 */
const IntakeModal: React.FC<IntakeModalProps> = ({ files, source, onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [status, setStatus] = useState<IntakeStatus | null>(null);
  const [renames, setRenames] = useState<Record<string, RenameEntry>>({});
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  const [customerChoice, setCustomerChoice] = useState<'existing' | 'new'>('new');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerType, setNewCustomerType] = useState<'customer' | 'prospect'>('customer');
  const [projectName, setProjectName] = useState('');
  const [projectNameTouched, setProjectNameTouched] = useState(false);

  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitStartRef = useRef<number>(Date.now());
  const initializedRef = useRef(false);

  // ── Session bootstrap + upload ──────────────────────────────────────────
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    (async () => {
      try {
        const res = await fetchWithActivity(`${API_BASE_URL}/api/intake/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
          body: JSON.stringify({ source }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to start intake');
        const newSessionId = data.sessionId as string;
        setSessionId(newSessionId);

        const initial: UploadingFile[] = files.map((file, i) => ({
          localId: `f${i}-${file.name}`,
          file,
          status: 'uploading',
        }));
        setUploadingFiles(initial);

        await Promise.all(
          initial.map(async (uf) => {
            try {
              const form = new FormData();
              form.append('file', uf.file);
              const uploadRes = await fetchWithActivity(`${API_BASE_URL}/api/intake/sessions/${newSessionId}/documents`, {
                method: 'POST',
                headers: authHeaders(token),
                body: form,
              });
              const uploadData = await uploadRes.json();
              if (!uploadRes.ok) throw new Error(uploadData.error || 'Upload failed');
              setUploadingFiles((prev) =>
                prev.map((f) => (f.localId === uf.localId ? { ...f, status: 'uploaded', documentId: uploadData.id } : f))
              );
              setRenames((prev) => ({
                ...prev,
                [uploadData.id]: { value: stripExtension(uf.file.name), touched: false },
              }));
            } catch (err) {
              setUploadingFiles((prev) =>
                prev.map((f) =>
                  f.localId === uf.localId
                    ? { ...f, status: 'error', errorMessage: err instanceof Error ? err.message : 'Upload failed' }
                    : f
                )
              );
            }
          })
        );
      } catch (err) {
        setUploadingFiles(
          files.map((file, i) => ({
            localId: `f${i}-${file.name}`,
            file,
            status: 'error',
            errorMessage: err instanceof Error ? err.message : 'Could not start intake',
          }))
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Poll extraction status while anything is pending ─────────────────────
  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetchWithActivity(`${API_BASE_URL}/api/intake/sessions/${sessionId}`, { headers: authHeaders(token) });
      if (!res.ok) return;
      const data: IntakeStatus = await res.json();
      setStatus(data);

      setRenames((prev) => {
        const next = { ...prev };
        for (const doc of data.documents) {
          const existing = next[doc.id];
          if (!existing) {
            next[doc.id] = { value: doc.suggestedTitle, touched: false };
          } else if (!existing.touched && doc.suggestedTitle && doc.suggestedTitle !== existing.value) {
            next[doc.id] = { value: doc.suggestedTitle, touched: false };
          }
        }
        return next;
      });

      if (!projectNameTouched && data.candidate.suggestedProjectName) {
        setProjectName(data.candidate.suggestedProjectName);
      }
      if (data.candidate.matches.length > 0 && !selectedCustomerId) {
        const best = data.candidate.matches[0];
        if (best.score >= 0.8) {
          setCustomerChoice('existing');
          setSelectedCustomerId(best.customerId);
        }
      }
      if (data.candidate.extractedName && !newCustomerName) {
        setNewCustomerName(data.candidate.extractedName);
      }

      // Keep polling until every dropped file has both landed as a document
      // row AND finished extraction — NOT just "pendingCount === 0", which is
      // trivially true (0 pending out of 0 known documents) on the very
      // first poll: it fires immediately on session creation, racing the
      // concurrent upload POSTs, which routinely haven't inserted their
      // document rows yet by the time this first response comes back.
      // `files` (the prop, not the `uploadingFiles` state) is the count to
      // compare against — it's non-empty and stable from the very first
      // render, so this can't itself race the same state-batching timing
      // it's guarding against.
      const allFilesAccountedFor = data.documents.length >= files.length;
      const stillWaiting = !allFilesAccountedFor || data.pendingCount > 0;
      const elapsed = Date.now() - waitStartRef.current;

      // Past the soft cap, stop making the user wait on the "Reading…"
      // copy — flip to manual entry so they can proceed — but keep polling
      // in the background up to the hard cap. Real Gemini extraction under
      // load has been observed taking 2+ minutes (see intake-service PR
      // notes), well past a 90s cap; a document that finishes at minute 2
      // must still retroactively populate the name/title once it lands,
      // not be silently dropped because polling already gave up.
      if (stillWaiting && elapsed >= EXTRACTION_SOFT_WAIT_MS) {
        setWaitedTooLong(true);
      }
      if (stillWaiting && elapsed < EXTRACTION_HARD_STOP_MS) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    } catch {
      // Best-effort — the modal still works from whatever it already has.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, token, files.length]);

  useEffect(() => {
    if (!sessionId) return;
    poll();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const uploadedDocIds = uploadingFiles.filter((f) => f.status === 'uploaded' && f.documentId).map((f) => f.documentId!);
  const allUploaded = uploadingFiles.length > 0 && uploadingFiles.every((f) => f.status !== 'uploading');
  const anyUploaded = uploadedDocIds.length > 0;

  const acceptAllSuggested = () => {
    if (!status) return;
    setRenames((prev) => {
      const next = { ...prev };
      for (const doc of status.documents) {
        next[doc.id] = { value: doc.suggestedTitle, touched: true };
      }
      return next;
    });
  };

  const removeFile = async (documentId: string) => {
    if (!sessionId) return;
    setUploadingFiles((prev) => prev.filter((f) => f.documentId !== documentId));
    setRenames((prev) => {
      const next = { ...prev };
      delete next[documentId];
      return next;
    });
    try {
      await fetchWithActivity(`${API_BASE_URL}/api/intake/sessions/${sessionId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
    } catch {
      // Non-critical — worst case an orphaned staged doc is reaped by the abandoned-session sweep.
    }
  };

  const handleCommit = async () => {
    if (!sessionId) return;
    if (customerChoice === 'existing' && !selectedCustomerId) {
      setCommitError('Pick an existing customer, or switch to "Create new".');
      return;
    }
    if (customerChoice === 'new' && !newCustomerName.trim()) {
      setCommitError('Enter a name for the new customer.');
      return;
    }
    if (!projectName.trim()) {
      setCommitError('Enter a folder name.');
      return;
    }

    setCommitting(true);
    setCommitError(null);
    try {
      const body: Record<string, any> = {
        projectName: projectName.trim(),
        renames: Object.entries(renames)
          .filter(([, entry]) => entry.value.trim())
          .map(([documentId, entry]) => ({ documentId, name: entry.value.trim() })),
      };
      if (customerChoice === 'existing') {
        body.customerId = selectedCustomerId;
      } else {
        body.newCustomer = { name: newCustomerName.trim(), customerType: newCustomerType };
      }

      const res = await fetchWithActivity(`${API_BASE_URL}/api/intake/sessions/${sessionId}/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to file these documents');
      setCommitResult(data as CommitResult);
      setStep(3);
    } catch (err) {
      setCommitError(err instanceof Error ? err.message : 'Failed to file these documents');
    } finally {
      setCommitting(false);
    }
  };

  const documentNamesById = new Map<string, string>(
    (status?.documents || []).map((d) => [d.id, renames[d.id]?.value || d.name])
  );

  const canProceedToStep2 = allUploaded && anyUploaded;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-secondary dark:text-dark-text">
                {step === 3 ? 'Filed and ready' : 'Set up from these documents'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Risk Ninja finds the customer and sets up the folder for you.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Step {step} of 3</span>
              <span>{step === 1 ? 'Files & names' : step === 2 ? 'Customer & folder' : 'Recommendations'}</span>
            </div>
            <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${(step / 3) * 100}%` }} />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {commitError && step !== 3 && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">{commitError}</div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {status && status.candidate.matches.length + 1 > 0 && status.documents.some((d) => d.suggestedTitle) && (
                <button
                  onClick={acceptAllSuggested}
                  className="text-sm text-primary hover:underline"
                >
                  Accept all suggested names
                </button>
              )}
              <div className="space-y-2">
                {uploadingFiles.map((f) => {
                  const docStatus = f.documentId ? status?.documents.find((d) => d.id === f.documentId) : undefined;
                  return (
                    <div key={f.localId} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
                      <div className="flex-shrink-0">
                        {f.status === 'uploading' && <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />}
                        {f.status === 'error' && <span className="text-red-500 text-lg">✕</span>}
                        {f.status === 'uploaded' && docStatus?.extractionStatus === 'processing' && (
                          <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        )}
                        {f.status === 'uploaded' && (docStatus?.extractionStatus === 'completed') && (
                          <span className="text-green-500 text-lg">✓</span>
                        )}
                        {f.status === 'uploaded' && (docStatus?.extractionStatus === 'failed' || docStatus?.extractionStatus === 'skipped') && (
                          <span title="Couldn't read this one — you can still file it" className="text-amber-500 text-lg">!</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {f.documentId ? (
                          <input
                            type="text"
                            value={renames[f.documentId]?.value ?? stripExtension(f.file.name)}
                            onChange={(e) =>
                              setRenames((prev) => ({
                                ...prev,
                                [f.documentId!]: { value: e.target.value, touched: true },
                              }))
                            }
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-secondary dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{f.file.name}</p>
                        )}
                        {f.status === 'error' && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{f.errorMessage}</p>}
                      </div>
                      {f.documentId && (
                        <button
                          onClick={() => removeFile(f.documentId!)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 text-sm"
                          title="Remove from this batch"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <p className="text-secondary dark:text-dark-text">
                  {status?.candidate.extractedName ? (
                    <>These look like documents for <strong>{status.candidate.extractedName}</strong>.</>
                  ) : status && status.pendingCount > 0 && !waitedTooLong ? (
                    `Reading ${status.pendingCount} of ${status.documents.length} document${status.documents.length === 1 ? '' : 's'}…`
                  ) : (
                    "We couldn't tell who these documents are for — pick or create the customer below."
                  )}
                </p>
                {status?.candidate.conflict && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                    Looks like more than one insured — you can remove files above and drop them separately.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                {(status?.candidate.matches || []).map((m) => (
                  <label key={m.customerId} className="flex items-center gap-2 p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40">
                    <input
                      type="radio"
                      name="intake-customer"
                      checked={customerChoice === 'existing' && selectedCustomerId === m.customerId}
                      onChange={() => {
                        setCustomerChoice('existing');
                        setSelectedCustomerId(m.customerId);
                      }}
                    />
                    <span className="text-sm text-secondary dark:text-dark-text">
                      Use existing: <strong>{m.name}</strong>
                      <span className="text-gray-400 ml-1">({Math.round(m.score * 100)}% match)</span>
                    </span>
                  </label>
                ))}

                <label className="flex items-start gap-2 p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <input
                    type="radio"
                    name="intake-customer"
                    checked={customerChoice === 'new'}
                    onChange={() => setCustomerChoice('new')}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <span className="text-sm text-secondary dark:text-dark-text">Create new customer</span>
                    {customerChoice === 'new' && (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newCustomerName}
                          onChange={(e) => setNewCustomerName(e.target.value)}
                          placeholder="Customer name"
                          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-secondary dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <select
                          value={newCustomerType}
                          onChange={(e) => setNewCustomerType(e.target.value as 'customer' | 'prospect')}
                          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-secondary dark:text-dark-text"
                        >
                          <option value="customer">Customer</option>
                          <option value="prospect">Prospect</option>
                        </select>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Folder name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => {
                    setProjectName(e.target.value);
                    setProjectNameTouched(true);
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-secondary dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}

          {step === 3 && commitResult && (
            <div className="space-y-4">
              <p className="text-sm text-secondary dark:text-dark-text">
                Filed {uploadedDocIds.length} document{uploadedDocIds.length === 1 ? '' : 's'}.
              </p>
              {commitResult.suggestions.length > 0 ? (
                <WorkflowSuggestionCards
                  suggestions={commitResult.suggestions}
                  documentNames={documentNamesById}
                  title="Recommended next"
                  onRun={(s) => {
                    navigate(suggestionDeepLink(commitResult.customerId, commitResult.projectId, s.suggestionKey));
                    onClose();
                  }}
                />
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No workflow recommendations yet — upload a few more documents from the folder to see suggestions.
                </p>
              )}
              <button
                onClick={() => {
                  navigate(`/customers/${commitResult.customerId}/projects/${commitResult.projectId}?tab=documents`);
                  onClose();
                }}
                className="text-sm text-primary hover:underline"
              >
                Go to folder →
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== 3 && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
            {step === 2 ? (
              <button onClick={() => setStep(1)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors">
                Previous
              </button>
            ) : (
              <span />
            )}
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStep2}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-60 transition-colors"
              >
                {committing ? 'Filing…' : 'File documents'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IntakeModal;
